<script>
	import { onMount } from "svelte";

	import Spinner from "../components/Spinner.svelte";
	import SpinnerLock from "../components/SpinnerLock.svelte";
	import { minDelay } from "../utils.js";
	import { apiPost } from '../api.js';
	import { MAX_EXPIRY_IN_MINUTES } from '../expiry.js';

	const base_url = window.location.origin;
	const url = "SECURESHARE_API_URL";
	const api_key = "SECURESHARE_API_KEY";
	const MAX_BURN_AFTER_READING = 5;
	const MAX_SECRET_LENGTH = 10000;
	const MAX_SECRET_FILE_SIZE_IN_BYTES = 524288000;
	let fileinput = $state();

	let { params } = $props();

	let linkId = "";
	let secretText = $state("");
	let savePromise = $state({});
	let saveRequested = $state(false);
	let validation_errors = $state([]);
	let linkValid = $state(null);
	let memo = $state("");
	let signedLinkPromise = $state({});
	let signedLink = {};
	let file_id = "";
	let file_name = "";
	let fileUploading = $state(false);
	let fileReady = $state(false);
	let fileError = $state(false);

	async function validateLink() {
		const res = await apiPost(`${url}/validate_guest_link`, { id: linkId }, { apiKey: api_key });
		if (res.ok) {
			linkValid = true;
			const json = await res.json();
			memo = json.memo || '';
		} else {
			linkValid = false;
		}
	}

	async function getS3PresignedSaveLink() {
		// Public variant — the guest link id is the capability that
		// authorizes the upload. The auth'd s3_presigned_save_generator
		// can't be used here because the recipient is unauthenticated.
		const res = await apiPost(`${url}/public_s3_presigned_save_generator`, { guest_link_id: linkId });
		signedLink = await res.json();
		if (res.ok) {
			return true;
		} else {
			throw new Error("Failed to get upload link");
		}
	}

	async function saveFileHttp(file) {
		let formData = new FormData();
		formData.append("key", signedLink.fields.key);
		formData.append("AWSAccessKeyId", signedLink.fields.AWSAccessKeyId);
		formData.append("policy", signedLink.fields.policy);
		formData.append("signature", signedLink.fields.signature);
		formData.append("x-amz-security-token", signedLink.fields["x-amz-security-token"]);
		formData.append("file", file);

		const res = await fetch(`${signedLink.url}`, {
			method: "POST",
			body: formData
		});

		if (res.ok) {
			file_id = signedLink.fields.key;
			return true;
		} else {
			throw new Error("Failed to upload file");
		}
	}

	const onFileSelected = async (e) => {
		if (!e.target.files[0]) return;
		if (e.target.files[0].size > MAX_SECRET_FILE_SIZE_IN_BYTES) {
			alert(`File is too large.  Maximum file size: ${MAX_SECRET_FILE_SIZE_IN_BYTES/1024/1024}MB`);
			e.target.value = '';
			return;
		}

		let file = e.target.files[0];
		file_name = file.name;
		fileUploading = true;
		fileReady = false;
		fileError = false;

		try {
			await saveFileHttp(file);
			fileReady = true;
		} catch {
			fileError = true;
		}
		fileUploading = false;
	}

    async function saveSecret() {
		const secretRequest = {
			guest_link_id: linkId,
			text: secretText,
			file_id: file_id || undefined,
			file_name: file_name || undefined,
			expiry_in_minutes: MAX_EXPIRY_IN_MINUTES,
			burn_after_reading: 10
		};

		const res = await apiPost(`${url}/public_save_sse`, secretRequest, { apiKey: api_key });
		const json = await res.json();
		if (res.ok) {
			return { "url" : json.url };
		} else {
			throw new Error(json);
		}
	}

	function sendSecret() {
		let valid = validate();
		if (valid) {
			saveRequested = true;
			savePromise = minDelay(saveSecret());
		}
	}

	function validate() {
		validation_errors = [];
		if (secretText.trim() === "") {
			validation_errors = [...validation_errors, "Text to share is required."];
		}

		return validation_errors.length === 0;
	}

	onMount(() => {
		linkId = params.linkId;
		validateLink();
		signedLinkPromise = getS3PresignedSaveLink();
	});
</script>

<main class="container">
	<div class:hidden={!saveRequested}>
		{#await savePromise}
			<SpinnerLock></SpinnerLock>
		{:then secret}
			<div class="card">
				<img src="/user-lock-solid.svg" aria-label="Response sent" alt="Response sent">
				<h1>Response sent!</h1>
				<p>Your response has been sent to the person who requested it.</p>
			</div>
		{:catch error}
			<p class="alert alert-danger">Failed to send response.</p>
		{/await}
	</div>
	{#if linkValid === false}
		<div class="alert alert-danger">This link is invalid or has expired.</div>
	{/if}
	<div class:hidden={saveRequested || linkValid === false}>
    	<h1>Respond to Message Request</h1>
		<p class="small">A CareEvolution employee has requested that you send them a secure message. Everything you enter below will be encrypted and only accessible by the person who sent you this link.</p>

		{#if memo}
			<div class="memo" aria-label="Note from the requester">
				<span class="memo-label">From the requester:</span>
				<p class="memo-text">{memo}</p>
			</div>
		{/if}

		<div class="form-item">
            <label for="secret_text" class="form-label">Message</label>
            <textarea name="secret_text" style="width: 100%; height: 400px" type="text" bind:value={secretText} maxlength={MAX_SECRET_LENGTH} class="form-control" autocomplete="off"></textarea>
        </div>

		<div class="form-item">
			{#await signedLinkPromise}
				<Spinner></Spinner>
			{:then res}
				<label for="secret_file" class="form-label">Attachment <span class="hint">(optional)</span></label>
				<input type="file" class="form-control file-input" onchange={(e)=>onFileSelected(e)} bind:this={fileinput} disabled={fileUploading}>
				{#if fileUploading}
					<span class="file-status uploading">Uploading...</span>
				{:else if fileReady}
					<span class="file-status ready">Ready</span>
				{:else if fileError}
					<span class="file-status error">Upload failed. Try again.</span>
				{/if}
			{:catch error}
				<p style="color: red">Cannot upload files at this time.</p>
			{/await}
		</div>

		{#if validation_errors.length > 0}
	    <div id="errors" class="alert alert-warning">
            <h2>Errors Found</h2>
    		<ul>
    		{#each validation_errors as err, i}
    			<li>{err}</li>
    		{/each}
    		</ul>
	    </div>
    {/if}
        <div>
	        <button onclick={sendSecret} class="btn btn-primary">Respond</button>
	        <a href="/" class="btn btn-outline-secondary" style="margin-left: .5rem">Cancel</a>
        </div>
	</div>

</main>

<style>
	.file-input {
		max-width: 400px;
		font-size: 85%;
	}

	.file-status {
		display: inline-block;
		margin-top: .5rem;
		font-size: 14px;
		font-weight: 600;
	}

	.file-status.uploading { color: #0d6efd; }
	.file-status.ready { color: #198754; }
	.file-status.error { color: #dc3545; }

    #errors h2 {
        font-size: 20px;
    }
	
	input[type=text], input[type=range] {
		width: 500px
	}
	
    input[type=range] {
		position: relative;
		top: 7px;
	}

	input[type=number] {
		width: 50px;
		padding-left: 0;
		padding-right: 0;
	}

	.card {
		border: 1px solid #e2e2e2;
		border-radius: .25rem;
		padding: 2rem 3rem 1rem 3rem;
		text-align: center;
		max-width: 400px;
		margin: 4rem auto;
	}

	.card img {
		height: 65px;
    	margin-bottom: 1rem;
	}

	.memo {
		margin: 1rem 0;
		padding: .75rem 1rem;
		border-left: 3px solid #0d6efd;
		background: #f0f6ff;
		border-radius: .25rem;
	}

	.memo-label {
		display: block;
		font-size: 12px;
		text-transform: uppercase;
		letter-spacing: .04em;
		color: #6c757d;
		margin-bottom: .25rem;
	}

	.memo-text {
		margin: 0;
		font-style: italic;
		color: #212529;
	}

</style>